import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { MODE_ENV } from "@/services/constants";
import { AuthRole, RestaurantRole, StageAuth } from "@/store/AuthStore";
import { rootStore } from "@/store/RootStore";
import { getSafeReturnUrl, RETURN_URL_PARAM } from "@/utils/returnUrl";

import { PATHS } from "./path";

const MODE = import.meta.env.VITE_MODE_APP;

const PublicRoute = observer(({ children }: { children: React.ReactNode }) => {
  const { stage, role, restaurantAccess, isAdmin } = rootStore.authStore;
  const [searchParams] = useSearchParams();
  const returnUrl = getSafeReturnUrl(searchParams.get(RETURN_URL_PARAM));

  const hasWrongPortalSession =
    stage === StageAuth.AUTHENTICATED &&
    ((MODE === MODE_ENV.ADMIN && !isAdmin) ||
      (MODE === MODE_ENV.PARTNER && isAdmin));

  useEffect(() => {
    if (hasWrongPortalSession) {
      rootStore.authStore.clearSession();
    }
  }, [hasWrongPortalSession]);

  if (stage === StageAuth.AUTHENTICATED) {
    if (hasWrongPortalSession) {
      return <>{children}</>;
    }

    if (returnUrl) {
      return <Navigate to={returnUrl} replace />;
    }

    if (MODE === MODE_ENV.ADMIN) {
      return <Navigate to={PATHS.ADMIN.ROOT} replace />;
    }

    if (MODE === MODE_ENV.PARTNER) {
      if (role === AuthRole.RESTAURANT) {
        switch (restaurantAccess) {
          case RestaurantRole.GROUP:
            return <Navigate to={PATHS.RESTAURANT.GROUP.ROOT} replace />;
          case RestaurantRole.BRAND:
            return <Navigate to={PATHS.RESTAURANT.BRAND.ROOT} replace />;
          case RestaurantRole.BRANCH:
            return <Navigate to={PATHS.RESTAURANT.BRANCH.ROOT} replace />;
          default:
            return <Navigate to={PATHS.RESTAURANT.ROOT} replace />;
        }
      }

      return <Navigate to={PATHS.RESTAURANT.ROOT} replace />;
    }

    if (isAdmin) {
      return <Navigate to={PATHS.ADMIN.ROOT} replace />;
    }

    if (role === AuthRole.RESTAURANT) {
      switch (restaurantAccess) {
        case RestaurantRole.GROUP:
          return <Navigate to={PATHS.RESTAURANT.GROUP.ROOT} replace />;
        case RestaurantRole.BRAND:
          return <Navigate to={PATHS.RESTAURANT.BRAND.ROOT} replace />;
        case RestaurantRole.BRANCH:
          return <Navigate to={PATHS.RESTAURANT.BRANCH.ROOT} replace />;
        default:
          return <Navigate to={PATHS.RESTAURANT.ROOT} replace />;
      }
    }

    return <Navigate to={PATHS.HOME} replace />;
  }

  return <>{children}</>;
});

export default PublicRoute;
